import React, { PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useScreenSize } from '@shared/hooks/use-screen-size'
import { runOnClient } from '@shared/pipes/client'
import { isClient } from '@shared/utils/is-client'
import cx from 'classnames'

import { Props } from './model'

import styles from './style.module.scss'

export const Carousel = ({ children, title }: PropsWithChildren<Props>) => {
	const sliderRef = useRef<HTMLDivElement>(null)
	const [offset, setOffset] = useState(0)
	const [childWidth, setChildWidth] = useState(0)
	const { isDesktop } = useScreenSize()
	const [currentScrollLeft, setCurrentScrollLeft] = useState(0)

	const computeSizes = useCallback(() => {
		runOnClient(() => {
			if (sliderRef.current) {
				setOffset(sliderRef.current.offsetLeft)
				setCurrentScrollLeft(sliderRef.current.scrollLeft)
				/**
				 * На данный момент скролл при использовании контролов навигации
				 * корректно работает только с учётом элементов списка одной ширины,
				 * за основу берётся первый найденный
				 */
				const child = sliderRef.current.querySelector('[role="listbox"]') as HTMLDivElement
				if (child) {
					setChildWidth(child.offsetLeft + child.offsetWidth)
				}
			}
		})
	}, [])

	const handleScrollSlider = useCallback(() => {
		setCurrentScrollLeft(sliderRef!.current!.scrollLeft)
	}, [])

	const handleScrollElement = useCallback(
		(direction: 'prev' | 'next') => {
			if (sliderRef.current) {
				if (direction === 'next') {
					sliderRef.current.scrollLeft += childWidth
				} else if (direction === 'prev') {
					sliderRef.current.scrollLeft -= childWidth
				}
			}
		},
		[childWidth]
	)

	const handleScrollPrev = useCallback(() => handleScrollElement('prev'), [handleScrollElement])
	const handleScrollNext = useCallback(() => handleScrollElement('next'), [handleScrollElement])

	useEffect(() => {
		if (!isClient) {
			return undefined
		}

		computeSizes()
		window.addEventListener('resize', computeSizes)
		sliderRef.current?.addEventListener('scroll', handleScrollSlider)

		return () => {
			window.removeEventListener('resize', computeSizes)
			sliderRef.current?.removeEventListener('scroll', handleScrollSlider)
		}
	}, [computeSizes])

	const isDisabledPrevArrow = useMemo(() => currentScrollLeft === 0, [currentScrollLeft])
	const isDisabledNextArrow = useMemo(() => {
		if (!sliderRef.current) return false

		return (
			sliderRef!.current!.scrollWidth! -
				currentScrollLeft -
				sliderRef.current?.getBoundingClientRect().width <=
			0
		)
	}, [currentScrollLeft])

	const showControls = useMemo(() => isDesktop, [isDesktop])

	const style = {
		marginRight: offset > 0 ? `-${offset}px` : `${offset}px`,
	}

	return (
		<div className={styles.overlay}>
			<div className={styles.header}>
				{title && title}
				{showControls && (
					<div className={styles.controls}>
						<div className={styles.navs}>
							<div
								tabIndex={0}
								onClick={handleScrollPrev}
								className={cx(styles.nav, { [styles.navDisabled]: isDisabledPrevArrow })}
							/>
							<div
								tabIndex={0}
								onClick={handleScrollNext}
								className={cx(styles.nav, { [styles.navDisabled]: isDisabledNextArrow })}
							/>
						</div>
					</div>
				)}
			</div>
			<div style={style} ref={sliderRef} role='slider' className={styles.wrapper}>
				{children}
			</div>
		</div>
	)
}

export const CarouselItem = ({
	children,
	className,
}: PropsWithChildren<{ className?: string }>) => (
	<div role='listbox'>
		<div className={cx(styles.item, className)}>{children}</div>
	</div>
)
